import React, { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import analyticsStyles from "../AdminAnalytics.module.scss";
import customStyles from "../AdminCustomers.module.scss";
import tableStyles from "./CustomerEngagementTable.module.scss";
import {
  AdminLicenseHolder,
  friendlyTierName,
} from "../admin-real-data.service";
import { formatRelativeActivity } from "../admin-formatting";
import { UIContext } from "../../../shared/shared-with-mobile/providers/ui.provider";
import ColumnsModal from "./ColumnsModal";
import Button from "../../../components/Button/Button";
import ListIcon from "../../../resources/icons/ListIcon";

// ============================================================
// Customer Engagement table — every license holder, searchable +
// sortable + customizable. Replaces the old "Top 10 Most Engaged
// Accounts" snippet with a full data view. Columns are organized into
// visual groups (Users, Questions Answered, Last Usage) separated by
// vertical borders.
//
// Favorite Views are pre-built sort presets shown as a prominent
// button row above the toolbar. They override the column-click sort
// when selected; clicking a column header afterward keeps the view
// label highlighted but applies the new sort.
// ============================================================

type FilterType = "number-min" | "text-contains" | "select";
type FilterOption = { value: string; label: string };

interface ColumnDef {
  id: string;
  label: string;
  // Optional group id this column belongs to. Adjacent columns sharing
  // the same groupId render under a single merged header in the top row.
  groupId?: string;
  defaultWidth: number;
  align?: "left" | "right" | "center";
  render: (h: AdminLicenseHolder) => React.ReactNode;
  sortValue: (h: AdminLicenseHolder) => string | number;
  // Filter declaration. The filter input renders in a row directly below
  // the column-label header. Empty / "any" / "" means no filter.
  filterType?: FilterType;
  filterOptions?: FilterOption[]; // for "select"
  filterValue?: (h: AdminLicenseHolder) => string | number | null | undefined;
  // Aggregate run over the currently filtered+sorted rows. Returns a
  // formatted string for display; columns without a defined aggregate
  // show "—" in the aggregate header row.
  aggregate?: (rows: AdminLicenseHolder[]) => string;
}

// Aggregate helpers — keep formatting consistent across columns.
function aggSum(
  rows: AdminLicenseHolder[],
  pick: (h: AdminLicenseHolder) => number | null | undefined,
  fmt: (n: number) => string = (n) => n.toLocaleString()
): string {
  let sum = 0;
  let any = false;
  for (const h of rows) {
    const v = pick(h);
    if (typeof v === "number" && isFinite(v)) {
      sum += v;
      any = true;
    }
  }
  return any ? fmt(sum) : "—";
}

function aggAvg(
  rows: AdminLicenseHolder[],
  pick: (h: AdminLicenseHolder) => number | null | undefined,
  fmt: (n: number) => string = (n) => Math.round(n).toLocaleString()
): string {
  let sum = 0;
  let count = 0;
  for (const h of rows) {
    const v = pick(h);
    if (typeof v === "number" && isFinite(v)) {
      sum += v;
      count += 1;
    }
  }
  return count > 0 ? fmt(sum / count) : "—";
}

function aggCountWhere(
  rows: AdminLicenseHolder[],
  pred: (h: AdminLicenseHolder) => boolean,
  label: string
): string {
  let n = 0;
  for (const h of rows) if (pred(h)) n += 1;
  return `${n.toLocaleString()} ${label}`;
}

function fmtMoney0(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return n.toLocaleString();
}

const GROUPS: Record<string, string> = {
  users: "Users",
  questions: "Questions Answered",
  lastUsage: "Last Usage",
  revenue: "Revenue",
  links: "External Links",
};

// Renders a clickable status pill for the Shopify / HubSpot link
// columns. "Yes" deep-links to the customer profile's relevant tab
// (Billing for Shopify, HubSpot tab for HubSpot); "Suggested" routes
// to the Import & Sync page where the candidate is awaiting approval;
// "No" is not clickable.
const LinkStatusPill: React.FC<{
  status: "yes" | "suggested" | "no" | undefined;
  platform: "shopify" | "hubspot";
  email: string;
}> = ({ status, platform, email }) => {
  const history = useHistory();
  const safeStatus: "yes" | "suggested" | "no" = status ?? "no";
  const config: Record<
    "yes" | "suggested" | "no",
    { label: string; color: string; bg: string; clickable: boolean }
  > = {
    yes: {
      label: "Yes",
      color: "#4CAF50",
      bg: "rgba(76,175,80,0.15)",
      clickable: true,
    },
    suggested: {
      label: "Suggested",
      color: "#FFC107",
      bg: "rgba(255,193,7,0.12)",
      clickable: true,
    },
    no: {
      label: "No",
      color: "#6f6f7e",
      bg: "transparent",
      clickable: false,
    },
  };
  const c = config[safeStatus];
  const onClick = (e: React.MouseEvent) => {
    if (!c.clickable) return;
    e.stopPropagation();
    if (safeStatus === "yes") {
      const tab = platform === "shopify" ? "billing" : "hubspot";
      history.push(`/admin/customer/${encodeURIComponent(email)}#${tab}`);
    } else if (safeStatus === "suggested") {
      // Send to the Sync UI; the platform-specific tab is selected
      // there but we don't yet deep-link to a specific group.
      history.push(`/admin/import`);
    }
  };
  return (
    <span
      onClick={onClick}
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: c.color,
        background: c.bg,
        cursor: c.clickable ? "pointer" : "default",
        userSelect: "none",
      }}
      title={
        c.clickable
          ? safeStatus === "yes"
            ? `Open ${platform} data on this profile`
            : `Review suggested ${platform} matches`
          : `No ${platform} link`
      }
    >
      {c.label}
    </span>
  );
};

// Per-column filter input. Renders a number / text / select input based
// on the column's filterType. stopPropagation on every event so column-
// header click handlers (sort, resize) don't fire when the admin is
// typing into the filter.
const FilterInput: React.FC<{
  col: ColumnDef;
  value: string;
  onChange: (val: string) => void;
}> = ({ col, value, onChange }) => {
  if (!col.filterType) return null;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  if (col.filterType === "select") {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={stop}
        onMouseDown={stop}
        className={tableStyles.filterSelect}
      >
        {(col.filterOptions ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  if (col.filterType === "number-min") {
    return (
      <input
        type="number"
        placeholder="≥"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={stop}
        onMouseDown={stop}
        className={tableStyles.filterInput}
      />
    );
  }
  return (
    <input
      type="text"
      placeholder="contains…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={stop}
      onMouseDown={stop}
      className={tableStyles.filterInput}
    />
  );
};

// Sort helper for the link-status columns: yes > suggested > no. Used
// as the column's sortValue so the most-actionable rows surface first
// when the user sorts by the status column.
function statusRank(s: "yes" | "suggested" | "no" | undefined): number {
  if (s === "yes") return 2;
  if (s === "suggested") return 1;
  return 0;
}

// Helper for the dropoff column: q90d - qPrev90d. Positive = growing,
// negative = declining.
function delta90d(h: AdminLicenseHolder): number | null {
  if (
    typeof h.questions90d !== "number" ||
    typeof h.questionsPrev90d !== "number"
  ) {
    return null;
  }
  return h.questions90d - h.questionsPrev90d;
}

const COLUMNS: ColumnDef[] = [
  {
    id: "owner",
    label: "Owner",
    defaultWidth: 180,
    render: (h) => <span className={analyticsStyles.bold}>{h.owner}</span>,
    sortValue: (h) => h.owner.toLowerCase(),
    filterType: "text-contains",
    filterValue: (h) => h.owner,
    aggregate: (rows) => `${rows.length.toLocaleString()} customers`,
  },
  {
    id: "teamName",
    label: "Team",
    defaultWidth: 200,
    render: (h) => {
      const name = h.teamName && h.teamName !== "-" ? h.teamName : "—";
      return <span>{name}</span>;
    },
    sortValue: (h) =>
      h.teamName && h.teamName !== "-" ? h.teamName.toLowerCase() : "zzz",
    filterType: "text-contains",
    filterValue: (h) => h.teamName,
  },
  {
    id: "location",
    label: "Location",
    defaultWidth: 140,
    render: (h) => {
      const parts = [h.teamCity, h.teamState].filter(
        (s) => typeof s === "string" && s.trim().length
      );
      if (parts.length === 0)
        return <span className={analyticsStyles.secondary}>—</span>;
      return (
        <span className={analyticsStyles.secondary}>{parts.join(", ")}</span>
      );
    },
    sortValue: (h) => {
      const parts = [h.teamState, h.teamCity].filter(
        (s) => typeof s === "string" && s.trim().length
      );
      return parts.join(",").toLowerCase() || "zzz";
    },
    filterType: "text-contains",
    filterValue: (h) => [h.teamCity, h.teamState].filter(Boolean).join(", "),
  },
  {
    id: "created",
    label: "Created",
    defaultWidth: 110,
    render: (h) => (
      <span className={analyticsStyles.secondary}>
        {formatRelativeActivity(h.dateCreated)}
      </span>
    ),
    sortValue: (h) => (h.dateCreated ? new Date(h.dateCreated).getTime() : 0),
  },
  {
    id: "coaches",
    label: "Coaches",
    groupId: "users",
    defaultWidth: 80,
    align: "right",
    render: (h) =>
      typeof h.totalCoaches === "number"
        ? h.totalCoaches.toLocaleString()
        : "—",
    sortValue: (h) => h.totalCoaches ?? -1,
    filterType: "number-min",
    filterValue: (h) => h.totalCoaches ?? 0,
    aggregate: (rows) => aggSum(rows, (h) => h.totalCoaches),
  },
  {
    id: "players",
    label: "Players",
    groupId: "users",
    defaultWidth: 80,
    align: "right",
    render: (h) =>
      typeof h.totalPlayers === "number"
        ? h.totalPlayers.toLocaleString()
        : "—",
    sortValue: (h) => h.totalPlayers ?? -1,
    filterType: "number-min",
    filterValue: (h) => h.totalPlayers ?? 0,
    aggregate: (rows) => aggSum(rows, (h) => h.totalPlayers),
  },
  {
    id: "qAllTime",
    label: "All Time",
    groupId: "questions",
    defaultWidth: 110,
    align: "right",
    render: (h) => h.questions.toLocaleString(),
    sortValue: (h) => h.questions,
    filterType: "number-min",
    filterValue: (h) => h.questions,
    aggregate: (rows) => aggSum(rows, (h) => h.questions, fmtCompact),
  },
  {
    id: "q1y",
    label: "Past 1y",
    groupId: "questions",
    defaultWidth: 90,
    align: "right",
    render: (h) =>
      typeof h.questions1y === "number" ? h.questions1y.toLocaleString() : "—",
    sortValue: (h) => h.questions1y ?? -1,
    filterType: "number-min",
    filterValue: (h) => h.questions1y ?? 0,
    aggregate: (rows) => aggSum(rows, (h) => h.questions1y, fmtCompact),
  },
  {
    id: "q90d",
    label: "Past 90d",
    groupId: "questions",
    defaultWidth: 90,
    align: "right",
    render: (h) =>
      typeof h.questions90d === "number"
        ? h.questions90d.toLocaleString()
        : "—",
    sortValue: (h) => h.questions90d ?? -1,
    filterType: "number-min",
    filterValue: (h) => h.questions90d ?? 0,
    aggregate: (rows) => aggSum(rows, (h) => h.questions90d, fmtCompact),
  },
  {
    id: "qPrev90d",
    label: "Prev 90d",
    groupId: "questions",
    defaultWidth: 90,
    align: "right",
    render: (h) =>
      typeof h.questionsPrev90d === "number" ? (
        <span className={analyticsStyles.secondary}>
          {h.questionsPrev90d.toLocaleString()}
        </span>
      ) : (
        "—"
      ),
    sortValue: (h) => h.questionsPrev90d ?? -1,
    filterType: "number-min",
    filterValue: (h) => h.questionsPrev90d ?? 0,
    aggregate: (rows) => aggSum(rows, (h) => h.questionsPrev90d, fmtCompact),
  },
  {
    id: "delta90d",
    label: "Δ 90d",
    groupId: "questions",
    defaultWidth: 90,
    align: "right",
    render: (h) => {
      const d = delta90d(h);
      if (d === null) return "—";
      const color = d > 0 ? "#4CAF50" : d < 0 ? "#F44336" : "#ababb7";
      const sign = d > 0 ? "+" : "";
      return (
        <span style={{ color }}>
          {sign}
          {d.toLocaleString()}
        </span>
      );
    },
    // For sort purposes, treat missing as -Infinity so they sink to
    // the bottom on descending sorts and don't pollute the "Gone Stale"
    // ascending sort with random rows.
    sortValue: (h) => {
      const d = delta90d(h);
      return d === null ? Number.NEGATIVE_INFINITY : d;
    },
    filterType: "number-min",
    filterValue: (h) => delta90d(h) ?? 0,
    aggregate: (rows) =>
      aggSum(
        rows,
        (h) => delta90d(h),
        (n) => {
          const sign = n > 0 ? "+" : "";
          return `${sign}${fmtCompact(n)}`;
        }
      ),
  },
  {
    id: "q7d",
    label: "Past 7d",
    groupId: "questions",
    defaultWidth: 90,
    align: "right",
    render: (h) =>
      typeof h.questions7d === "number" ? h.questions7d.toLocaleString() : "—",
    sortValue: (h) => h.questions7d ?? -1,
    filterType: "number-min",
    filterValue: (h) => h.questions7d ?? 0,
    aggregate: (rows) => aggSum(rows, (h) => h.questions7d, fmtCompact),
  },
  {
    id: "commsTotal",
    label: "Comms",
    groupId: "comms",
    defaultWidth: 90,
    align: "right",
    render: (h) => {
      if (typeof h.commsTotal !== "number") return "—";
      if (h.commsTotal === 0) {
        return <span style={{ color: "#6f6f7e" }}>0</span>;
      }
      const out = h.commsOutbound ?? 0;
      const inb = h.commsInbound ?? 0;
      const log = h.commsLogged ?? 0;
      const tooltip =
        `${h.commsTotal} total — ` +
        `outbound: ${out} (SendGrid sends + HubSpot OUTBOUND emails/calls), ` +
        `inbound: ${inb} (HubSpot INBOUND emails/calls; platform inbound not wired yet), ` +
        `logged: ${log} (HubSpot notes + meetings)`;
      // Color-code: green for high engagement, yellow for some, gray for tiny
      const color =
        h.commsTotal >= 50
          ? "#4CAF50"
          : h.commsTotal >= 10
          ? "#FFC107"
          : "#ababb7";
      return (
        <span
          title={tooltip}
          style={{ color, fontWeight: 600, cursor: "help" }}
        >
          {h.commsTotal.toLocaleString()}
        </span>
      );
    },
    sortValue: (h) => h.commsTotal ?? -1,
    filterType: "number-min",
    filterValue: (h) => h.commsTotal ?? 0,
    aggregate: (rows) => aggSum(rows, (h) => h.commsTotal, fmtCompact),
  },
  {
    id: "shopifyPaid",
    label: "Shopify $",
    groupId: "revenue",
    defaultWidth: 110,
    align: "right",
    render: (h) => {
      if (typeof h.shopifyPaidTotal !== "number") return "—";
      if (h.shopifyPaidTotal === 0) {
        return <span style={{ color: "#6f6f7e" }}>$0</span>;
      }
      return (
        <span style={{ color: "#4CAF50", fontWeight: 600 }}>
          $
          {h.shopifyPaidTotal.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </span>
      );
    },
    sortValue: (h) => h.shopifyPaidTotal ?? -1,
    filterType: "number-min",
    filterValue: (h) => h.shopifyPaidTotal ?? 0,
    aggregate: (rows) => aggSum(rows, (h) => h.shopifyPaidTotal, fmtMoney0),
  },
  {
    id: "shopifyLink",
    label: "Shopify",
    groupId: "links",
    defaultWidth: 100,
    align: "center",
    render: (h) => (
      <LinkStatusPill
        status={h.shopifyLink}
        platform="shopify"
        email={h.email}
      />
    ),
    sortValue: (h) => statusRank(h.shopifyLink),
    filterType: "select",
    filterOptions: [
      { value: "", label: "Any" },
      { value: "yes", label: "Yes" },
      { value: "suggested", label: "Suggested" },
      { value: "no", label: "No" },
    ],
    filterValue: (h) => h.shopifyLink ?? "no",
    aggregate: (rows) =>
      aggCountWhere(rows, (h) => h.shopifyLink === "yes", "linked"),
  },
  {
    id: "hubspotLink",
    label: "HubSpot",
    groupId: "links",
    defaultWidth: 100,
    align: "center",
    render: (h) => (
      <LinkStatusPill
        status={h.hubspotLink}
        platform="hubspot"
        email={h.email}
      />
    ),
    sortValue: (h) => statusRank(h.hubspotLink),
    filterType: "select",
    filterOptions: [
      { value: "", label: "Any" },
      { value: "yes", label: "Yes" },
      { value: "suggested", label: "Suggested" },
      { value: "no", label: "No" },
    ],
    filterValue: (h) => h.hubspotLink ?? "no",
    aggregate: (rows) =>
      aggCountWhere(rows, (h) => h.hubspotLink === "yes", "linked"),
  },
  {
    id: "lastCoach",
    label: "Coach",
    groupId: "lastUsage",
    defaultWidth: 130,
    render: (h) => (
      <span className={analyticsStyles.secondary}>
        {formatRelativeActivity(h.lastCoachActivityAt)}
      </span>
    ),
    sortValue: (h) =>
      h.lastCoachActivityAt ? new Date(h.lastCoachActivityAt).getTime() : 0,
  },
  {
    id: "lastPlayer",
    label: "Player",
    groupId: "lastUsage",
    defaultWidth: 130,
    render: (h) => (
      <span className={analyticsStyles.secondary}>
        {formatRelativeActivity(h.lastPlayerActivityAt)}
      </span>
    ),
    sortValue: (h) =>
      h.lastPlayerActivityAt ? new Date(h.lastPlayerActivityAt).getTime() : 0,
  },
  {
    id: "health",
    label: "Health",
    defaultWidth: 90,
    align: "right",
    render: (h) => {
      const cls =
        h.healthLabel === "Healthy"
          ? analyticsStyles.green
          : h.healthLabel === "At Risk"
          ? analyticsStyles.yellow
          : analyticsStyles.red;
      return (
        <span className={`${analyticsStyles.healthPill} ${cls}`}>
          {h.healthScore}
        </span>
      );
    },
    sortValue: (h) => h.healthScore,
    filterType: "number-min",
    filterValue: (h) => h.healthScore,
    aggregate: (rows) => aggAvg(rows, (h) => h.healthScore),
  },
  // Email + Tier columns are kept available so admins can add them back
  // via the Columns modal, but they're hidden by default — Team / Location
  // / usage stats / link status / health do more to identify a customer
  // at a glance.
  {
    id: "email",
    label: "Email",
    defaultWidth: 220,
    render: (h) => <span className={analyticsStyles.secondary}>{h.email}</span>,
    sortValue: (h) => h.email.toLowerCase(),
    filterType: "text-contains",
    filterValue: (h) => h.email,
  },
  {
    id: "tier",
    label: "Tier",
    defaultWidth: 130,
    render: (h) => (
      <span className={analyticsStyles.tierBadge}>
        {friendlyTierName(h.tier)}
      </span>
    ),
    sortValue: (h) => friendlyTierName(h.tier),
    filterType: "text-contains",
    filterValue: (h) => friendlyTierName(h.tier),
  },
];

// Columns hidden by default — admins can re-show them via the Columns
// modal. Surfaced down here so the COLUMNS array reads top-to-bottom
// as the visual order shipped users see on first load.
const DEFAULT_HIDDEN_COLUMNS = ["email", "tier"];

// Default column order for fresh admins (no stored columnOrder). Decoupled
// from the COLUMNS array so the visual order can change without reshuffling
// the column definitions. Columns not in this list fall through to the end
// (preserving definition order).
const DEFAULT_COLUMN_ORDER = [
  "owner",
  "teamName",
  "shopifyPaid",
  "shopifyLink",
  "hubspotLink",
  "location",
  "created",
  "coaches",
  "players",
  "qAllTime",
  "q1y",
  "q90d",
  "qPrev90d",
  "delta90d",
  "q7d",
  "commsTotal",
  "lastCoach",
  "lastPlayer",
  "health",
  "email",
  "tier",
];

const PREF_KEYS = {
  order: "admin.engagement.columnOrder",
  hidden: "admin.engagement.hiddenColumns",
  widths: "admin.engagement.columnWidths",
  rowCount: "admin.engagement.rowCount",
  view: "admin.engagement.favoriteView",
  filters: "admin.engagement.filters",
};

const ROW_COUNT_OPTIONS = [5, 10, 25, 30, 50, 100, 500];
const DEFAULT_ROW_COUNT = 30;

// Favorite Views — pre-built sort presets surfaced above the toolbar.
type FavoriteView =
  | "topRecent"
  | "newAccounts"
  | "goneStale"
  | "hotTrials"
  | "atRisk";
const DEFAULT_VIEW: FavoriteView = "topRecent";

interface FavoriteViewDef {
  id: FavoriteView;
  label: string;
  description: string;
  // Apply this preset by setting the matching column's sort. The view
  // selector calls applyView() which sets sortKey + sortAsc, and merges
  // presetFilters into the filter state. Filter keys not in presetFilters
  // are preserved so admins can stack a view on top of their own filter.
  sortKey: string;
  sortAsc: boolean;
  presetFilters?: Record<string, string>;
}

const FAVORITE_VIEWS: FavoriteViewDef[] = [
  {
    id: "topRecent",
    label: "Top Recent Users",
    description: "Most questions answered in the past 90 days",
    sortKey: "q90d",
    sortAsc: false,
  },
  {
    id: "newAccounts",
    label: "New Accounts",
    description: "Most recently created accounts",
    sortKey: "created",
    sortAsc: false,
  },
  {
    id: "goneStale",
    label: "Gone Stale",
    description: "Largest dropoff in question volume (past 90d vs prior 90d)",
    sortKey: "delta90d",
    sortAsc: true,
  },
  {
    id: "hotTrials",
    label: "Hot Trials",
    description:
      "Trials with the highest 7-day question volume — call list for sales",
    sortKey: "q7d",
    sortAsc: false,
    presetFilters: { tier: "Trial" },
  },
  {
    id: "atRisk",
    label: "At-Risk Renewals",
    description:
      "Largest engagement dropoffs (90d vs prior 90d). Sales/CS save list — combine with a tier filter for paying customers.",
    sortKey: "delta90d",
    sortAsc: true,
  },
];

function loadStringArray(key: string, fallback: string[]): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((s) => typeof s === "string")) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return fallback;
}
function loadWidthMap(key: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "number" && v > 0) out[k] = v;
      }
      return out;
    }
  } catch {
    // ignore
  }
  return {};
}
function loadStringMap(key: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "string") out[k] = v;
      }
      return out;
    }
  } catch {
    // ignore
  }
  return {};
}

function loadNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  } catch {
    // ignore
  }
  return fallback;
}
function loadFavoriteView(key: string): FavoriteView {
  try {
    const raw = localStorage.getItem(key);
    if (
      raw === "topRecent" ||
      raw === "newAccounts" ||
      raw === "goneStale" ||
      raw === "hotTrials" ||
      raw === "atRisk"
    ) {
      return raw;
    }
  } catch {
    // ignore
  }
  return DEFAULT_VIEW;
}
function persist<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Safari private mode / quota — state still applies for the
    // current session, just won't survive a reload.
  }
}

interface Props {
  holders: AdminLicenseHolder[];
}

const CustomerEngagementTable: React.FC<Props> = ({ holders }) => {
  const history = useHistory();
  const { dispatchModal, closeModal } = useContext(UIContext);

  const [search, setSearch] = useState("");
  const [activeView, setActiveView] = useState<FavoriteView>(() =>
    loadFavoriteView(PREF_KEYS.view)
  );
  const initialPreset =
    FAVORITE_VIEWS.find((v) => v.id === activeView) ?? FAVORITE_VIEWS[0];
  const [sortKey, setSortKey] = useState<string>(initialPreset.sortKey);
  const [sortAsc, setSortAsc] = useState<boolean>(initialPreset.sortAsc);
  const [rowCount, setRowCount] = useState<number>(() =>
    loadNumber(PREF_KEYS.rowCount, DEFAULT_ROW_COUNT)
  );

  const [filters, setFilters] = useState<Record<string, string>>(() =>
    loadStringMap(PREF_KEYS.filters)
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    loadStringArray(PREF_KEYS.order, DEFAULT_COLUMN_ORDER)
  );
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(
    () => new Set(loadStringArray(PREF_KEYS.hidden, DEFAULT_HIDDEN_COLUMNS))
  );
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    loadWidthMap(PREF_KEYS.widths)
  );

  const visibleColumns = useMemo<ColumnDef[]>(() => {
    const seen = new Set<string>();
    const ordered: ColumnDef[] = [];
    for (const id of columnOrder) {
      if (seen.has(id)) continue;
      const col = COLUMNS.find((c) => c.id === id);
      if (col && !hiddenIds.has(id)) {
        ordered.push(col);
        seen.add(id);
      }
    }
    for (const col of COLUMNS) {
      if (seen.has(col.id) || hiddenIds.has(col.id)) continue;
      ordered.push(col);
      seen.add(col.id);
    }
    return ordered;
  }, [columnOrder, hiddenIds]);

  // Build runs of adjacent same-group columns for the merged top header.
  // If a user reorders columns and breaks a group apart, the group label
  // appears once per run (gracefully degrades instead of breaking the
  // header layout).
  const headerRuns = useMemo(() => {
    const runs: {
      groupId: string | null;
      count: number;
      label: string | null;
    }[] = [];
    let currentGroup: string | null = null;
    let runStart = 0;
    for (let i = 0; i < visibleColumns.length; i++) {
      const groupId = visibleColumns[i].groupId ?? null;
      if (groupId !== currentGroup) {
        if (i > runStart) {
          runs.push({
            groupId: currentGroup,
            count: i - runStart,
            label: currentGroup ? GROUPS[currentGroup] ?? null : null,
          });
        }
        currentGroup = groupId;
        runStart = i;
      }
    }
    if (runStart < visibleColumns.length) {
      runs.push({
        groupId: currentGroup,
        count: visibleColumns.length - runStart,
        label: currentGroup ? GROUPS[currentGroup] ?? null : null,
      });
    }
    return runs;
  }, [visibleColumns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = holders;
    if (q) {
      rows = rows.filter(
        (h) =>
          h.owner.toLowerCase().includes(q) ||
          h.email.toLowerCase().includes(q) ||
          h.teamName.toLowerCase().includes(q) ||
          friendlyTierName(h.tier).toLowerCase().includes(q)
      );
    }
    // Per-column filters: numeric ≥, text contains, exact-match select.
    for (const [colId, raw] of Object.entries(filters)) {
      if (!raw) continue;
      const col = COLUMNS.find((c) => c.id === colId);
      if (!col?.filterType || !col.filterValue) continue;
      if (col.filterType === "number-min") {
        const min = parseFloat(raw);
        if (!isFinite(min)) continue;
        rows = rows.filter((h) => {
          const v = col.filterValue!(h);
          return typeof v === "number" && v >= min;
        });
      } else if (col.filterType === "text-contains") {
        const needle = raw.toLowerCase();
        rows = rows.filter((h) => {
          const v = col.filterValue!(h);
          return typeof v === "string" && v.toLowerCase().includes(needle);
        });
      } else if (col.filterType === "select") {
        rows = rows.filter((h) => {
          const v = col.filterValue!(h);
          return v === raw;
        });
      }
    }
    const sortCol = COLUMNS.find((c) => c.id === sortKey);
    if (sortCol) {
      const sign = sortAsc ? 1 : -1;
      rows = rows.slice().sort((a, b) => {
        const av = sortCol.sortValue(a);
        const bv = sortCol.sortValue(b);
        if (typeof av === "number" && typeof bv === "number") {
          return (av - bv) * sign;
        }
        return String(av).localeCompare(String(bv)) * sign;
      });
    }
    return rows;
  }, [holders, search, filters, sortKey, sortAsc]);

  const setSort = (key: string) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      const col = COLUMNS.find((c) => c.id === key);
      const sample = holders[0] ?? ({} as AdminLicenseHolder);
      const numeric = col && typeof col.sortValue(sample) === "number";
      setSortAsc(!numeric);
    }
  };
  const sortIndicator = (key: string) =>
    sortKey === key ? (sortAsc ? " ↑" : " ↓") : "";

  const widthFor = (col: ColumnDef): number =>
    columnWidths[col.id] ?? col.defaultWidth;

  const beginResize = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    columnId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const col = COLUMNS.find((c) => c.id === columnId);
    if (!col) return;
    const startWidth = columnWidths[columnId] ?? col.defaultWidth;
    const onMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      // Reduced minimum so admins can squeeze Team / Owner columns when
      // they want to fit more visible columns on screen.
      const next = Math.max(30, startWidth + delta);
      setColumnWidths((prev) => ({ ...prev, [columnId]: next }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setColumnWidths((prev) => {
        persist(PREF_KEYS.widths, prev);
        return prev;
      });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const openColumnsModal = () => {
    dispatchModal({
      title: "Customize columns",
      open: true,
      size: "medium",
      body: (
        <ColumnsModal
          allColumns={COLUMNS.map((c) => ({ id: c.id, label: c.label }))}
          visibleIds={visibleColumns.map((c) => c.id)}
          saveCallback={(orderedVisible) => {
            const visibleSet = new Set(orderedVisible);
            const hiddenInOrder = columnOrder.filter(
              (id) => !visibleSet.has(id)
            );
            for (const c of COLUMNS) {
              if (!visibleSet.has(c.id) && !hiddenInOrder.includes(c.id)) {
                hiddenInOrder.push(c.id);
              }
            }
            const newOrder = [...orderedVisible, ...hiddenInOrder];
            const newHidden = new Set<string>(hiddenInOrder);
            setColumnOrder(newOrder);
            setHiddenIds(newHidden);
            persist(PREF_KEYS.order, newOrder);
            persist(PREF_KEYS.hidden, Array.from(newHidden));
            closeModal();
          }}
        />
      ),
    });
  };

  const resetWidths = () => {
    setColumnWidths({});
    try {
      localStorage.removeItem(PREF_KEYS.widths);
    } catch {
      // ignore
    }
  };

  const onRowCountChange = (n: number) => {
    setRowCount(n);
    try {
      localStorage.setItem(PREF_KEYS.rowCount, String(n));
    } catch {
      // ignore
    }
  };

  const setFilter = (colId: string, val: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (val === "") delete next[colId];
      else next[colId] = val;
      persist(PREF_KEYS.filters, next);
      return next;
    });
  };

  const clearFilters = () => {
    setFilters({});
    try {
      localStorage.removeItem(PREF_KEYS.filters);
    } catch {
      // ignore
    }
  };

  const setView = (id: FavoriteView) => {
    setActiveView(id);
    const preset = FAVORITE_VIEWS.find((v) => v.id === id);
    if (preset) {
      setSortKey(preset.sortKey);
      setSortAsc(preset.sortAsc);
      // Merge presetFilters into the current filter state. Filter keys
      // not in presetFilters are preserved so admins can stack a view
      // on top of their own ad-hoc filters.
      if (preset.presetFilters) {
        setFilters((prev) => ({ ...prev, ...preset.presetFilters }));
      }
    }
    try {
      localStorage.setItem(PREF_KEYS.view, id);
    } catch {
      // ignore
    }
  };

  return (
    <>
      {/* Favorite Views — prominent preset selector. Sets sort to a
          curated view of the data without having to know which column
          to click. */}
      <div className={tableStyles.favoriteViews}>
        <span className={tableStyles.favoriteViewsLabel}>Quick Views</span>
        <div className={tableStyles.favoriteViewsBtns}>
          {FAVORITE_VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`${tableStyles.viewBtn} ${
                activeView === v.id ? tableStyles.viewBtnActive : ""
              }`}
              onClick={() => setView(v.id)}
              title={v.description}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className={tableStyles.toolbar}>
        <input
          type="text"
          placeholder="Search by name, email, team, or tier..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={tableStyles.searchInput}
        />
        <label className={tableStyles.rowCountLabel}>
          Rows:
          <select
            value={rowCount}
            onChange={(e) => onRowCountChange(Number(e.target.value))}
            className={tableStyles.rowCountSelect}
          >
            {ROW_COUNT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <Button
          theme="tertiary"
          size="small"
          icon={<ListIcon />}
          onClick={openColumnsModal}
        >
          Columns
        </Button>
        <Button theme="tertiary" size="small" onClick={resetWidths}>
          Reset Widths
        </Button>
        {Object.keys(filters).length > 0 && (
          <Button theme="tertiary" size="small" onClick={clearFilters}>
            Clear Filters ({Object.keys(filters).length})
          </Button>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          className={`${analyticsStyles.miniTable} ${customStyles.resizableTable} ${tableStyles.engagementTable}`}
        >
          <colgroup>
            {visibleColumns.map((col) => (
              <col key={col.id} style={{ width: widthFor(col) }} />
            ))}
          </colgroup>
          <thead>
            {/* Top row: merged group headers. Cells with no groupId
                render an empty placeholder <th /> so column widths
                stay aligned across both header rows. */}
            <tr className={tableStyles.groupRow}>
              {headerRuns.map((run, i) => {
                const isLast = i === headerRuns.length - 1;
                return (
                  <th
                    key={`grp-${i}`}
                    colSpan={run.count}
                    className={`${tableStyles.groupCell} ${
                      run.label ? tableStyles.groupCellLabeled : ""
                    } ${!isLast ? tableStyles.groupSeparator : ""}`}
                  >
                    {run.label ?? ""}
                  </th>
                );
              })}
            </tr>
            {/* Sub-header row: per-column labels with sort hooks. */}
            <tr>
              {visibleColumns.map((col, i) => {
                // Mark the right edge of each group-run with a separator
                // border so visible columns within a group read as one
                // band.
                const next = visibleColumns[i + 1];
                const groupEnds =
                  !!col.groupId && (!next || next.groupId !== col.groupId);
                const groupStarts =
                  !!col.groupId &&
                  (i === 0 || visibleColumns[i - 1].groupId !== col.groupId);
                return (
                  <th
                    key={col.id}
                    className={`${customStyles.resizableTh} ${
                      groupStarts ? tableStyles.groupColStart : ""
                    } ${groupEnds ? tableStyles.groupColEnd : ""}`}
                    style={{ textAlign: col.align ?? "left" }}
                  >
                    <span
                      onClick={() => setSort(col.id)}
                      className={customStyles.thLabel}
                    >
                      {col.label}
                      {sortIndicator(col.id)}
                    </span>
                    <span
                      className={customStyles.resizeHandle}
                      onMouseDown={(e) => beginResize(e, col.id)}
                      title="Drag to resize column"
                    />
                  </th>
                );
              })}
            </tr>
            {/* Filter row — per-column inputs. Numeric columns get a
                "≥" min input, text columns get a contains input, status
                columns get a select. Empty input = no filter. Resize
                handle is repeated on every header row so admins can
                grab the column edge from any of them. */}
            <tr className={tableStyles.filterRow}>
              {visibleColumns.map((col) => (
                <th
                  key={col.id}
                  style={{
                    textAlign: col.align ?? "left",
                    position: "relative",
                  }}
                  className={`${tableStyles.filterCell} ${customStyles.resizableTh}`}
                >
                  <FilterInput
                    col={col}
                    value={filters[col.id] ?? ""}
                    onChange={(val) => setFilter(col.id, val)}
                  />
                  <span
                    className={customStyles.resizeHandle}
                    onMouseDown={(e) => beginResize(e, col.id)}
                    title="Drag to resize column"
                  />
                </th>
              ))}
            </tr>
            {/* Aggregate row — Total / Avg / Count per column based on
                whatever is currently filtered into view. Columns without
                a defined aggregate show "—". */}
            <tr className={tableStyles.aggregateRow}>
              {visibleColumns.map((col) => (
                <th
                  key={col.id}
                  style={{
                    textAlign: col.align ?? "left",
                    position: "relative",
                  }}
                  className={`${tableStyles.aggregateCell} ${customStyles.resizableTh}`}
                >
                  {col.aggregate ? col.aggregate(filtered) : "—"}
                  <span
                    className={customStyles.resizeHandle}
                    onMouseDown={(e) => beginResize(e, col.id)}
                    title="Drag to resize column"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, rowCount).map((h) => (
              <tr
                key={h.email}
                onClick={() =>
                  history.push(`/admin/customer/${encodeURIComponent(h.email)}`)
                }
                style={{ cursor: "pointer" }}
              >
                {visibleColumns.map((col, i) => {
                  const next = visibleColumns[i + 1];
                  const groupEnds =
                    !!col.groupId && (!next || next.groupId !== col.groupId);
                  const groupStarts =
                    !!col.groupId &&
                    (i === 0 || visibleColumns[i - 1].groupId !== col.groupId);
                  return (
                    <td
                      key={col.id}
                      style={{ textAlign: col.align ?? "left" }}
                      className={`${customStyles.cell} ${
                        groupStarts ? tableStyles.groupColStart : ""
                      } ${groupEnds ? tableStyles.groupColEnd : ""}`}
                    >
                      {col.render(h)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length > rowCount && (
        <p
          className={analyticsStyles.subtitle}
          style={{ marginTop: 12, textAlign: "center" }}
        >
          Showing {rowCount} of {filtered.length.toLocaleString()} matching
          customers. Increase row count or refine search to see more.
        </p>
      )}
    </>
  );
};

export default CustomerEngagementTable;
